import React, { useState } from 'react';
import { Routes, Route, Navigate, useNavigate, Link, useLocation } from 'react-router-dom';
import { Bot, Terminal, User, Settings, BarChart2, Globe, Home, ChevronDown } from 'lucide-react'; 
import { useTranslation } from 'react-i18next'; // 👈 Хук для переводов

// Импорт компонентов
import { Auth } from './components/Auth';
import { LandingPage } from './pages/LandingPage';
import { Platform } from './components/Platform'; 
import { AdminPanel } from './components/AdminPanel'; 
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';

import { ApiSettings } from './components/ApiSettings';
import { ProfilePage } from './pages/ProfilePage';
import { BacktestPage } from './pages/BacktestPage';
import { ReportsPage } from './pages/ReportsPage';

// --- ОБЕРТКА ДЛЯ КОНТЕНТА ---
const PageLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="animate-in fade-in duration-500">
    {children}
  </div>
);

// --- СПИСОК ЯЗЫКОВ ---
const LANGUAGES = [
  { code: 'en', label: 'English', flag: 'GB' },
  { code: 'ru', label: 'Русский', flag: 'RU' },
  { code: 'de', label: 'Deutsch', flag: 'DE' },
  { code: 'fr', label: 'Français', flag: 'FR' },
  { code: 'es', label: 'Español', flag: 'ES' },
  { code: 'zh', label: '中文', flag: 'CN' },   
  { code: 'ja', label: '日本語', flag: 'JP' }, 
  { code: 'ko', label: '한국어', flag: 'KR' }, 
];

// --- КОМПОНЕНТ ВЫБОРА ЯЗЫКА ---
const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = LANGUAGES.find(l => i18n.language?.startsWith(l.code)) || LANGUAGES[0];

  const toggleLang = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-bold px-2 py-2 border border-transparent hover:border-slate-700 rounded-lg"
      >
        <Globe size={18} />
        <span className="hidden xl:inline">{currentLang.label}</span>
        <span className="xl:hidden uppercase font-mono text-xs">{currentLang.flag}</span> 
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* ВЫПАДАЮЩЕЕ МЕНЮ (z-[110]) */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-40 bg-[#0f172a] border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-[110] animate-in fade-in zoom-in-95 duration-200">
          <div className="py-1">
            {LANGUAGES.map((lang) => (
              <button 
                key={lang.code}
                onClick={() => toggleLang(lang.code)} 
                className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center gap-3 hover:bg-slate-800 ${i18n.language?.startsWith(lang.code) ? 'text-blue-400 bg-slate-800/50' : 'text-slate-300'}`}
              >
                <span className="font-mono text-xs opacity-70 w-5">{lang.flag}</span>
                <span className="font-medium">{lang.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// --- LAYOUT (Меню навигации) ---
const ProtectedLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { t } = useTranslation(); // 👈 Подключаем перевод
  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 font-sans pb-20">
      {/* Навигационная панель (z-[100]) */}
      <nav className="border-b border-slate-800 bg-[#0f172a]/90 backdrop-blur-md sticky top-0 z-[100]">
        <div className="max-w-[1920px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          
          {/* ЛЕВАЯ ЧАСТЬ: ЛОГОТИП */}
          <div 
            className="font-bold text-white text-lg md:text-xl flex items-center gap-2 cursor-pointer"
            onClick={() => window.location.href = '/platform'}
          >
            <div className="bg-blue-600/20 p-1.5 rounded-lg">
               <Bot className="text-blue-500 w-5 h-5" />
            </div>
            <span className="hidden md:inline">TradingBot<span className="text-blue-500">Pro</span></span>
          </div>

          {/* ПРАВАЯ ЧАСТЬ: МЕНЮ */}
          <div className="flex items-center gap-1 md:gap-2">
            
            <Link 
              to="/platform" 
              className={`px-3 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${isActive('/platform') ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
               <Terminal size={18} /> 
               <span className="hidden lg:inline">{t('nav.terminal')}</span> {/* 👈 Перевод */}
            </Link>

            <Link 
              to="/reports" 
              className={`px-3 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${isActive('/reports') ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <BarChart2 size={18} /> 
              <span className="hidden lg:inline">{t('platform.nav.reports') || 'Reports'}</span>
            </Link>
            
            <Link 
              to="/backtest" 
              className={`px-3 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${isActive('/backtest') ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
               <BarChart2 size={18} /> 
               <span className="hidden lg:inline">{t('nav.backtest')}</span> {/* 👈 Перевод */}
            </Link>

            <Link 
              to="/profile" 
              className={`px-3 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${isActive('/profile') ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
               <User size={18} /> 
               <span className="hidden lg:inline">{t('nav.cabinet')}</span> {/* 👈 Перевод */}
            </Link>

            <Link 
              to="/settings" 
              className={`px-3 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${isActive('/settings') ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
               <Settings size={18} /> 
               <span className="hidden lg:inline">{t('nav.api')}</span> {/* 👈 Перевод */}
            </Link>

            {/* РАЗДЕЛИТЕЛЬ */}
            <div className="h-6 w-px bg-slate-700 mx-2 hidden md:block"></div>

            {/* WEBSITE LINK */}
            <a 
              href="/" 
              className="px-3 py-2 rounded-lg text-sm font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-all flex items-center gap-2"
            >
               <Home size={18} /> 
               <span className="hidden lg:inline">{t('nav.website')}</span> {/* 👈 Перевод */}
            </a>

            {/* ЯЗЫК */}
            <LanguageSelector />

          </div>
        </div>
      </nav>

      {/* Контент страницы */}
      <div className="max-w-[1920px] mx-auto">
         {children}
      </div>
    </div>
  );
};

function App() {
  const navigate = useNavigate();
  const isAuthenticated = !!localStorage.getItem('token');

  const handleLogin = () => {
    navigate('/platform');
  };

  const ProtectedRoute = ({ children }: { children: any }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    return <ProtectedLayout>{children}</ProtectedLayout>;
  };

  return (
    <Routes>
      {/* PUBLIC ROUTES */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/platform" /> : <Auth onLogin={handleLogin} />
      } />
      
      <Route path="/register" element={
        isAuthenticated ? <Navigate to="/platform" /> : <Auth onLogin={handleLogin} isRegister />
      } />

      {/* PROTECTED ROUTES */}
      <Route path="/platform" element={<ProtectedRoute><Platform /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><div className="p-6 md:p-12"><ApiSettings /></div></ProtectedRoute>} />
      
      {/* Исправленный маршрут отчетов (теперь под защитой) */}
      <Route path="/reports" element={
        <ProtectedRoute>
          <PageLayout>
            <ReportsPage />
          </PageLayout>
        </ProtectedRoute>
      } />

      {/* Backtest Page */}
      <Route path="/backtest" element={
        <ProtectedRoute>
          <PageLayout>
            <BacktestPage />
          </PageLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;